import {useEffect} from "react";
import {useLocation} from "react-router";
import {absoluteUrl, isPrivatePath, SITE_DESCRIPTION, SITE_NAME} from "../seo";
import {format, meta as messages} from "../i18n";

export interface PageMeta {
    // "<title> – Tristar Advanced Software" in the browser tab and search results; just the site name without one.
    title?: string;
    // For search results and link previews: a sentence or two of plain text.
    description?: string;
    // The page's canonical path, when it isn't the address it was reached by (/docs/astrel is /docs/ASTREL).
    path?: string;
    // An absolute URL, for link previews.
    image?: string;
    // schema.org data (https://schema.org), for search engines and agents.
    jsonLd?: object | object[];
}

// The page's <title>, description, canonical link, Open Graph tags and structured data. index.html has the defaults;
// SiteLayout puts them back on each navigation (resetPageMeta), and the page then sets its own. Only what's given is
// set, so a page can call this more than once (PageHeader sets the title, the page the rest). The prerendered pages
// (scripts/prerender.mjs) keep whatever the page set, so crawlers that don't run scripts see it too.
export function usePageMeta(meta: PageMeta): void {
    const {pathname} = useLocation();
    const key = JSON.stringify(meta);
    useEffect(() => {
        const {title, description, path, image, jsonLd} = meta;
        if (title !== undefined) {
            const full = title ? format(messages.pageTitle, { title, site: SITE_NAME }) : SITE_NAME;
            document.title = full;
            setMeta('property', 'og:title', full);
        }
        if (description !== undefined) {
            const text = truncate(description, 300);
            setMeta('name', 'description', text);
            setMeta('property', 'og:description', text);
        }
        if (path !== undefined) {
            setCanonical(path);
        }
        if (image !== undefined) {
            setMeta('property', 'og:image', image);
            setMeta('name', 'twitter:card', 'summary_large_image');
        }
        if (jsonLd !== undefined) {
            setJsonLd(jsonLd);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [key, pathname]);
}

// Back to the site's defaults, for a new page: the address it was reached by is its canonical one until it says
// otherwise. Private pages (and ones that aren't there) aren't indexed.
export function resetPageMeta(pathname: string, notFound = false): void {
    document.title = SITE_NAME;
    setMeta('property', 'og:title', SITE_NAME);
    setMeta('name', 'description', SITE_DESCRIPTION);
    setMeta('property', 'og:description', SITE_DESCRIPTION);
    setMeta('property', 'og:image', undefined);
    setMeta('name', 'twitter:card', 'summary');
    setMeta('name', 'robots', notFound || isPrivatePath(pathname) ? 'noindex' : undefined);
    setCanonical(pathname);
    setJsonLd(undefined);
}

// Pages that aren't there say so (they're served with a 404 too, once prerendered).
export function useNotFoundMeta(): void {
    useNoIndexMeta();
}

// Pages that stand in for the real one (not found, down for maintenance) aren't indexed, and have no canonical address.
export function useNoIndexMeta(): void {
    const {pathname} = useLocation();
    useEffect(() => {
        setMeta('name', 'robots', 'noindex');
        setCanonical(undefined);
    }, [pathname]);
}

function setMeta(attribute: 'name' | 'property', name: string, content: string | undefined): void {
    let element = document.head.querySelector<HTMLMetaElement>(`meta[${attribute}="${name}"]`);
    if (content === undefined) {
        element?.remove();
        return;
    }
    if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attribute, name);
        document.head.appendChild(element);
    }
    element.content = content;
}

function setCanonical(path: string | undefined): void {
    let link = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (path === undefined) {
        link?.remove();
        setMeta('property', 'og:url', undefined);
        return;
    }
    // One address per page: no trailing slash (except the home page's) or query.
    const url = absoluteUrl(path === '/' ? '/' : path.replace(/\/+$/, ''));
    if (!link) {
        link = document.createElement('link');
        link.rel = 'canonical';
        document.head.appendChild(link);
    }
    link.href = url;
    setMeta('property', 'og:url', url);
}

// An inline script, but data, not code: the CSP doesn't apply to it.
function setJsonLd(data: object | object[] | undefined): void {
    let script = document.getElementById('page-jsonld');
    if (data === undefined) {
        script?.remove();
        return;
    }
    if (!script) {
        script = document.createElement('script');
        script.id = 'page-jsonld';
        script.setAttribute('type', 'application/ld+json');
        document.head.appendChild(script);
    }
    // </script> in a value can't end the element early.
    script.textContent = JSON.stringify(Array.isArray(data) ? { '@context': 'https://schema.org', '@graph': data } : { '@context': 'https://schema.org', ...data })
        .replace(/</g, '\\u003c');
}

function truncate(text: string, length: number): string {
    const clean = text.replace(/\s+/g, ' ').trim();
    return clean.length <= length ? clean : `${clean.substring(0, length - 1).replace(/\s+\S*$/, '')}…`;
}
